import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import {
  FolderKanban,
  FileText,
  Users,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowRight,
  Plus,
  UserCheck,
  ClipboardList,
  Eye,
  Shield
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingProjects, setPendingProjects] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, chartRes, usersRes, projectsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers: getAuthHeader() }),
        axios.get(`${API}/analytics/chart-data`, { headers: getAuthHeader() }),
        axios.get(`${API}/users/pending`, { headers: getAuthHeader() }),
        axios.get(`${API}/projects?status=pending_review`, { headers: getAuthHeader() }),
        axios.get(`${API}/reports?status=pending_admin_review`, { headers: getAuthHeader() })
      ]);
      setAnalytics(analyticsRes.data);
      setChartData(chartRes.data);
      setPendingUsers(usersRes.data);
      setPendingProjects(projectsRes.data);
      setPendingReports(reportsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`, {}, { headers: getAuthHeader() });
      toast.success('User approved successfully');
      fetchData();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/reject`, {}, { headers: getAuthHeader() });
      toast.success('User rejected');
      fetchData();
    } catch (error) {
      console.error('Error rejecting user:', error);
      toast.error('Failed to reject user');
    }
  };

  const handleApproveProject = async (projectId) => {
    try {
      await axios.put(`${API}/projects/${projectId}/approve`, {}, { headers: getAuthHeader() });
      toast.success('Project approved');
      fetchData();
    } catch (error) {
      console.error('Error approving project:', error);
      toast.error('Failed to approve project');
    }
  };

  const CHART_COLORS = ['#0F172A', '#10B981', '#64748B', '#F59E0B', '#EF4444', '#8B5CF6'];

  const getStatusLabel = (status) => {
    const labels = {
      'pending_review': 'Pending Review',
      'approved': 'Approved',
      'agent_assigned': 'Agent Assigned',
      'in_progress': 'In Progress',
      'verification_complete': 'Verification Complete',
      'completed': 'Completed',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const totalPending = (analytics?.pending_approvals || 0) + pendingProjects.length + pendingReports.length;

  const statCards = [
    {
      title: 'Total Projects',
      value: analytics?.total_projects || 0,
      subtitle: `${analytics?.in_progress_projects || 0} in progress`,
      icon: FolderKanban,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Pending Actions',
      value: totalPending,
      subtitle: 'Requires attention',
      icon: Clock,
      color: 'bg-amber-100 text-amber-600',
      urgent: totalPending > 0
    },
    {
      title: 'Active Agents',
      value: analytics?.total_agents || 0,
      subtitle: `${analytics?.pending_approvals || 0} pending approval`,
      icon: Users,
      color: 'bg-emerald-100 text-emerald-600'
    },
    {
      title: 'Budget Tracked',
      value: `$${((analytics?.total_budget || 0) / 1000).toFixed(0)}k`,
      subtitle: `${Math.round(analytics?.budget_utilization || 0)}% utilized`,
      icon: DollarSign,
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  return (
    <div className="space-y-8 page-enter" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope']">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            {user?.is_super_admin && (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                Super Admin
              </span>
            )}
          </div>
          <p className="text-slate-500">Manage users, projects, and verification workflow.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" asChild>
            <Link to="/admin/users">
              Manage Users
            </Link>
          </Button>
          <Button asChild>
            <Link to="/admin/projects">
              View All Projects
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className={`stat-card hover-lift ${stat.urgent ? 'ring-2 ring-amber-400' : ''}`}
            data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1 font-['Manrope']">{stat.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{stat.subtitle}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending Actions Tabs */}
      {totalPending > 0 && (
        <Card className="stat-card border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Pending Actions ({totalPending})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="users" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="users" className="gap-2">
                  <UserCheck className="w-4 h-4" />
                  Users ({pendingUsers.length})
                </TabsTrigger>
                <TabsTrigger value="projects" className="gap-2">
                  <FolderKanban className="w-4 h-4" />
                  Projects ({pendingProjects.length})
                </TabsTrigger>
                <TabsTrigger value="reports" className="gap-2">
                  <FileText className="w-4 h-4" />
                  Reports ({pendingReports.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="users">
                {pendingUsers.length > 0 ? (
                  <div className="space-y-3">
                    {pendingUsers.map((u) => (
                      <div
                        key={u.user_id}
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200"
                        data-testid={`pending-user-${u.user_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${
                            u.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'
                          }`}>
                            {u.role === 'admin' ? (
                              <Shield className="w-5 h-5 text-purple-600" />
                            ) : (
                              <ClipboardList className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                            {u.role === 'agent' && u.agent_profile && (
                              <p className="text-xs text-slate-400 mt-1">
                                {u.agent_profile.field_of_expertise} • {u.agent_profile.years_of_experience || 0} years exp.
                              </p>
                            )}
                            {u.admin_request_reason && (
                              <p className="text-xs text-purple-600 mt-1">
                                Reason: {u.admin_request_reason}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`status-badge ${
                            u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {u.role === 'admin' ? 'Admin Request' : 'Field Agent'}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectUser(u.user_id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveUser(u.user_id)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`approve-user-${u.user_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No pending user approvals</p>
                )}
              </TabsContent>

              <TabsContent value="projects">
                {pendingProjects.length > 0 ? (
                  <div className="space-y-3">
                    {pendingProjects.map((p) => (
                      <div
                        key={p.project_id}
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200"
                        data-testid={`pending-project-${p.project_id}`}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{p.title}</p>
                          <p className="text-sm text-slate-500">
                            by {p.donor_name} • {p.organization_name}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {p.country} • Budget: ${(p.budget || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" asChild>
                            <Link to={`/admin/projects/${p.project_id}`}>
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Link>
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveProject(p.project_id)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`approve-project-${p.project_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No projects pending review</p>
                )}
              </TabsContent>

              <TabsContent value="reports">
                {pendingReports.length > 0 ? (
                  <div className="space-y-3">
                    {pendingReports.map((r) => (
                      <div
                        key={r.report_id}
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200"
                        data-testid={`pending-report-${r.report_id}`}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{r.title}</p>
                          <p className="text-sm text-slate-500">
                            by {r.agent_name} • {r.project_title}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Submitted: {new Date(r.submitted_at || r.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" asChild>
                            <Link to={`/admin/reports`}>
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No reports pending review</p>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Project Status Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData?.project_status?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={chartData.project_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="count"
                    nameKey="status"
                    label={({ status, count }) => `${getStatusLabel(status)}: ${count}`}
                  >
                    {chartData.project_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, getStatusLabel(name)]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-slate-400">
                No project data yet
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Budget by Project</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData?.budget_data?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData.budget_data} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Bar dataKey="budget" fill="#0F172A" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="spent" fill="#10B981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-slate-400">
                No budget data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="stat-card border-l-4 border-l-emerald-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Completed Projects</p>
            <p className="text-3xl font-bold text-emerald-600 mt-2 font-['Manrope']">
              {analytics?.completed_projects || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Released Reports</p>
            <p className="text-3xl font-bold text-blue-600 mt-2 font-['Manrope']">
              {analytics?.released_reports || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Total Donors</p>
            <p className="text-3xl font-bold text-purple-600 mt-2 font-['Manrope']">
              {analytics?.total_donors || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-slate-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Avg. Progress</p>
            <p className="text-3xl font-bold text-slate-600 mt-2 font-['Manrope']">
              {Math.round(analytics?.avg_progress || 0)}%
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
